// src/utils/api.js
import axios from 'axios';

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

// Attach JWT token to every request
api.interceptors.request.use(config => {
  const token = localStorage.getItem('rm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Global error handling
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('rm_token');
      localStorage.removeItem('rm_admin');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// ---- Convenience wrappers -----------------------------------

export const auth = {
  login:          (data)    => api.post('/auth/login', data),
  me:             ()        => api.get('/auth/me'),
  changePassword: (data)    => api.post('/auth/change-password', data)
};

export const students = {
  list:    (params) => api.get('/students', { params }),
  get:     (id)     => api.get(`/students/${id}`),
  create:  (data)   => api.post('/students', data),
  update:  (id, d)  => api.put(`/students/${id}`, d),
  delete:  (id)     => api.delete(`/students/${id}`)
};

export const devices = {
  list:        (params) => api.get('/devices', { params }),
  get:         (id)     => api.get(`/devices/${id}`),
  create:      (data)   => api.post('/devices', data),
  update:      (id, d)  => api.put(`/devices/${id}`, d),
  delete:      (id)     => api.delete(`/devices/${id}`),
  setStatus:   (id, st, reason) => api.patch(`/devices/${id}/status`, { status: st, reason }),
  ouiLookup:   (mac)    => api.get('/devices/tools/oui-lookup', { params: { mac } })
};

export const dashboard = {
  stats:      () => api.get('/dashboard/stats'),
  suspicious: () => api.get('/dashboard/suspicious')
};

export const radius = {
  status:    ()     => api.get('/radius/status'),
  authTest:  (mac)  => api.post('/radius/auth-test', { mac_address: mac }),
  nasList:   ()     => api.get('/radius/nas'),
  createNas: (data) => api.post('/radius/nas', data),
  deleteNas: (id)   => api.delete(`/radius/nas/${id}`),
  accounting:()     => api.get('/radius/accounting'),
  postauth:  ()     => api.get('/radius/postauth'),
  sync:      ()     => api.post('/radius/sync')
};

export const ml = {
  insights:    () => api.get('/ml/insights'),
  regPatterns: () => api.get('/ml/registration-patterns'),
  anomaly:     () => api.get('/ml/anomaly-detection'),
  clusters:    () => api.get('/ml/clusters'),
  deviceInsights:() => api.get('/ml/device-insights')
};

export const reports = {
  students:    (fmt) => api.get('/reports/students',    { params: { format: fmt }, responseType: fmt === 'csv' ? 'blob' : 'json' }),
  devices:     (fmt) => api.get('/reports/devices',     { params: { format: fmt }, responseType: fmt === 'csv' ? 'blob' : 'json' }),
  auditLogs:   (fmt) => api.get('/reports/audit-logs',  { params: { format: fmt }, responseType: fmt === 'csv' ? 'blob' : 'json' }),
  accounting:  (fmt) => api.get('/reports/radius-accounting', { params: { format: fmt }, responseType: fmt === 'csv' ? 'blob' : 'json' })
};

export const settings = {
  courses:      ()        => api.get('/settings/courses'),
  createCourse: (d)       => api.post('/settings/courses', d),
  updateCourse: (id, d)   => api.put(`/settings/courses/${id}`, d),
  deleteCourse: (id)      => api.delete(`/settings/courses/${id}`),
  institutions: ()        => api.get('/settings/institutions'),
  createInst:   (d)       => api.post('/settings/institutions', d),
  updateInst:   (id, d)   => api.put(`/settings/institutions/${id}`, d),
  deleteInst:   (id)      => api.delete(`/settings/institutions/${id}`),
  academicYears:()        => api.get('/settings/academic-years'),
  createYear:   (d)       => api.post('/settings/academic-years', d),
  updateYear:   (id, d)   => api.put(`/settings/academic-years/${id}`, d),
  deleteYear:   (id)      => api.delete(`/settings/academic-years/${id}`),
  appSettings:      ()    => api.get('/settings/app'),
  updateAppSettings:(d)   => api.put('/settings/app', d)
};

export const admins = {
  list:   ()        => api.get('/admins'),
  create: (data)    => api.post('/admins', data),
  toggle: (id)      => api.patch(`/admins/${id}/toggle`),
  delete: (id)      => api.delete(`/admins/${id}`)
};

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href    = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
